import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CATALOGO_COMPORTAMENTI_PIN12, ParametriRegolazioneSchema, type CodiceComportamento, type ParametriRegolazione } from '@emacomfort/domain';
import { useDevice } from '@/app/DeviceProvider';
import { useAvvisi } from '@/app/useAvvisi';
import { Avviso, Bottone, Campo, Card, Interruttore, Pagina, Select, stili } from '@/ui';
import { useTecnico } from '../comune';
import s from '../features.module.css';

type Gruppo = Exclude<keyof ParametriRegolazione, 'comportamentoPin12'>;

interface CampoDef {
  gruppo: Gruppo;
  chiave: string;
  sotto?: string;
  unita: string;
  passo: number;
}

const CAMPI: CampoDef[] = [
  { gruppo: 'curvaClimatica', chiave: 'pendenza', unita: 'kelvinPerGrado', passo: 0.1 },
  { gruppo: 'curvaClimatica', chiave: 'temperaturaBase', unita: 'gradi', passo: 0.5 },
  { gruppo: 'curvaClimatica', chiave: 'temperaturaEsternaProgetto', unita: 'gradi', passo: 1 },
  { gruppo: 'curvaClimatica', chiave: 'mandataMin', unita: 'gradi', passo: 1 },
  { gruppo: 'curvaClimatica', chiave: 'mandataMax', unita: 'gradi', passo: 1 },
  { gruppo: 'isteresi', chiave: 'zona', unita: 'gradi', passo: 0.1 },
  { gruppo: 'isteresi', chiave: 'generatore', unita: 'gradi', passo: 0.5 },
  { gruppo: 'isteresi', chiave: 'tempoMinimoOnMin', unita: 'minuti', passo: 1 },
  { gruppo: 'isteresi', chiave: 'tempoMinimoOffMin', unita: 'minuti', passo: 1 },
  { gruppo: 'deltaT', chiave: 'nominale', unita: 'gradi', passo: 0.5 },
  { gruppo: 'deltaT', chiave: 'sogliaAllarme', unita: 'gradi', passo: 0.5 },
  { gruppo: 'deltaT', chiave: 'minutiPrimaDellAllarme', unita: 'minuti', passo: 1 },
  { gruppo: 'circolatore', chiave: 'portataMinimaPct', unita: 'pct', passo: 5 },
  { gruppo: 'sanitario', chiave: 'setpoint', unita: 'gradi', passo: 1 },
  { gruppo: 'sanitario', chiave: 'isteresi', unita: 'gradi', passo: 1 },
  { gruppo: 'sanitario', chiave: 'tettoPrioritaMin', unita: 'minuti', passo: 5 },
  { gruppo: 'sanitario', chiave: 'antilegionellaTemperatura', sotto: 'antilegionella.temperatura', unita: 'gradi', passo: 1 },
  { gruppo: 'sanitario', chiave: 'antilegionellaGiorno', sotto: 'antilegionella.giorno', unita: '', passo: 1 },
  { gruppo: 'sanitario', chiave: 'antilegionellaOra', sotto: 'antilegionella.ora', unita: 'ore', passo: 1 },
  { gruppo: 'cascata', chiave: 'ritardoSecondarioMin', unita: 'minuti', passo: 5 },
  { gruppo: 'protezioni', chiave: 'antigeloZona', unita: 'gradi', passo: 1 },
  { gruppo: 'protezioni', chiave: 'antigeloEsterna', unita: 'gradi', passo: 1 },
  { gruppo: 'protezioni', chiave: 'sovratemperaturaDelta', unita: 'gradi', passo: 1 },
  { gruppo: 'protezioni', chiave: 'antibloccaggioGiorni', unita: 'giorni', passo: 1 },
  { gruppo: 'protezioni', chiave: 'margineRugiada', unita: 'gradi', passo: 0.5 },
  { gruppo: 'commutazioneStagionale', chiave: 'sogliaRiscaldamento', unita: 'gradi', passo: 1 },
  { gruppo: 'commutazioneStagionale', chiave: 'sogliaRaffrescamento', unita: 'gradi', passo: 1 },
  { gruppo: 'commutazioneStagionale', chiave: 'ritardoOre', unita: 'ore', passo: 1 },
  { gruppo: 'vmc', chiave: 'sogliaUmidita', unita: 'pct', passo: 5 },
];

const GRUPPI: Gruppo[] = ['curvaClimatica', 'isteresi', 'deltaT', 'circolatore', 'sanitario', 'cascata', 'protezioni', 'commutazioneStagionale', 'vmc'];

function leggi(p: ParametriRegolazione, c: CampoDef): number {
  const percorso = (c.sotto ?? c.chiave).split('.');
  let v: unknown = p[c.gruppo];
  percorso.forEach((k) => {
    v = (v as Record<string, unknown>)[k];
  });
  return v as number;
}

function scrivi(p: ParametriRegolazione, c: CampoDef, valore: number): ParametriRegolazione {
  const copia = structuredClone(p);
  const percorso = (c.sotto ?? c.chiave).split('.');
  let o: Record<string, unknown> = copia[c.gruppo] as unknown as Record<string, unknown>;
  percorso.slice(0, -1).forEach((k) => {
    o = o[k] as Record<string, unknown>;
  });
  o[percorso[percorso.length - 1]!] = valore;
  return copia;
}

export function ParametriPage() {
  const { t } = useTranslation();
  const { esegui } = useTecnico();
  const { store, client } = useDevice();
  const mostra = useAvvisi((a) => a.mostra);
  const [parametri, setParametri] = useState<ParametriRegolazione | null>(null);
  const [originali, setOriginali] = useState<string>('');
  const [errore, setErrore] = useState(false);

  useEffect(() => {
    void esegui((tec, sess) => tec.getParametri(sess)).then((p) => {
      if (p) {
        setParametri(p);
        setOriginali(JSON.stringify(p));
      }
    });
  }, [esegui]);

  if (!parametri) return null;
  const cambiato = JSON.stringify(parametri) !== originali;
  const valido = ParametriRegolazioneSchema.safeParse(parametri).success;

  const salva = async () => {
    if (!valido) {
      setErrore(true);
      return;
    }
    setErrore(false);
    const ok = await esegui(async (tec, sess) => {
      await tec.patchParametri(sess, parametri);
      return true;
    });
    if (ok) {
      setOriginali(JSON.stringify(parametri));
      store.setState({ impianto: await client.getImpianto() });
      mostra(t('tecnico.parametri.salvato'), 'ok');
    }
  };

  return (
    <Pagina
      titolo={t('tecnico.parametri.titolo')}
      sottotitolo={t('tecnico.parametri.sottotitolo')}
      azione={
        <Bottone variante="primario" disabled={!cambiato} onClick={() => void salva()}>
          {t('tecnico.parametri.salva')}
        </Bottone>
      }
    >
      <Avviso tono="warn">{t('tecnico.parametri.avviso')}</Avviso>
      {errore && <Avviso tono="err">{t('tecnico.parametri.nonValidi')}</Avviso>}
      <Card titolo={t('tecnico.parametri.gruppi.comportamentoPin12')}>
        <p className={s.letturaNota}>{t('tecnico.parametri.pin12Nota')}</p>
        <Select value={parametri.comportamentoPin12} onChange={(e) => setParametri({ ...parametri, comportamentoPin12: Number(e.target.value) as CodiceComportamento })}>
          {CATALOGO_COMPORTAMENTI_PIN12.map((v) => (
            <option key={v.codice} value={v.codice}>
              {v.codice} — {t(`tecnico.${v.chiave}`)}
            </option>
          ))}
        </Select>
      </Card>
      {GRUPPI.map((g) => (
        <Card key={g} titolo={t(`tecnico.parametri.gruppi.${g}`)}>
          <div className={s.campiGriglia}>
            {g === 'circolatore' && (
              <Campo etichetta={t('tecnico.parametri.campi.modalita')}>
                <Select value={parametri.circolatore.modalita} onChange={(e) => setParametri({ ...parametri, circolatore: { ...parametri.circolatore, modalita: e.target.value as 'FISSO' | 'MODULANTE' } })}>
                  <option value="FISSO">{t('tecnico.parametri.circolatoreModalita.FISSO')}</option>
                  <option value="MODULANTE">{t('tecnico.parametri.circolatoreModalita.MODULANTE')}</option>
                </Select>
              </Campo>
            )}
            {g === 'sanitario' && (
              <div className={stili.campo}>
                <span className={stili.etichetta}>{t('tecnico.parametri.campi.prioritaSuRiscaldamento')}</span>
                <Interruttore
                  acceso={parametri.sanitario.prioritaSuRiscaldamento}
                  onChange={(v) => setParametri({ ...parametri, sanitario: { ...parametri.sanitario, prioritaSuRiscaldamento: v } })}
                  etichetta={t('tecnico.parametri.campi.prioritaSuRiscaldamento')}
                  testoAcceso={t('comune.si')}
                  testoSpento={t('comune.no')}
                />
              </div>
            )}
            {CAMPI.filter((c) => c.gruppo === g).map((c) => (
              <Campo key={c.chiave} etichetta={`${t(`tecnico.parametri.campi.${c.chiave}`)}${c.unita ? ` (${t(`tecnico.parametri.unita.${c.unita}`)})` : ''}`}>
                <input className={stili.input} type="number" inputMode="decimal" step={c.passo} value={leggi(parametri, c)} onChange={(e) => setParametri(scrivi(parametri, c, Number(e.target.value)))} />
              </Campo>
            ))}
          </div>
        </Card>
      ))}
    </Pagina>
  );
}
